import { useState, useEffect, useMemo, useCallback } from 'react';
import { styled } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import CircularProgress from '@mui/material/CircularProgress';
import { Navigate } from 'react-router-dom';
import MyEncountersSearchContext from './MyEncountersSearchContext';
import MyEncountersSearchForm from './MyEncountersSearchForm';
import Comments from 'components/Comments/Comments';
import VoidDialog from './VoidDialog';
import MyEncountersTable from './MuiEnhancedTable';
import { ROUTES } from 'constants/routes';
import SessionTimeout from 'components/SessionTimeout';
import { ExceptionsDialog } from 'components/ExceptionsDialog/ExceptionsDialog';
import { useAuthentication } from 'hooks/useAuthentication';
import { ErrorResponse } from 'types/network';
import { ActionOwnerType } from 'types/EncounterSearch';
import { Divider } from '@mui/material';
import { theme } from 'lib/theme';
import { useMswManager } from 'lib/util/useMswManager';
import { HrznGatewayMswBuilder } from 'stories/HrznGatewayMswBuilder';
import { useMutation } from 'react-query';
import { useAuthenticatedAxios } from 'hooks/useAuthenticatedAxios';
import { useSnackbar } from 'notistack';
import { usePartnerGetStatusList } from 'hooks/requests/usePartnerGetStatusList';
import { usePartnerVoidEncounter } from 'hooks/requests/usePartnerVoidEncounter';
import { usePartnerGetEncounterHeaderVoidReasonList } from 'hooks/requests/usePartnerGetEncounterHeaderVoidReasonList';
import makeData from './makeData';

const MyEncounters = () => {
  const { data: encounterComments, mutateAsync: getEncounterComments } =
    usePartnerGetMyEncounterComments();
  const authenticatedAxios = useAuthenticatedAxios();
  const [encounters, setEncounters] = useState<any>([]);
  const [facilityOptions, setFacilityOptions] = useState<any>([]);
  const [facility, setFacility] = useState([]);
  const [status, setStatus] = useState('On Hold');
  const [statusList, setStatusList] = useState([]);
  const [advancedFilter, setAdvancedFilter] = useState('-- Select --');
  const [value1, setValue1] = useState('');
  const [value2, setValue2] = useState('');
  const [openComment, setOpenComment] = useState(null);
  const [openCommentDOS, setOpenCommentDOS] = useState<any>(null);
  const [comments, setComments] = useState<any>([]);
  const [search, setSearch] = useState(false);
  const [notSearched, setNotSearched] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [currentStatus, setCurrentStatus] = useState(status);
  const [submitComment, setSubmitComment] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [refetch, setRefetch] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<any>({});
  const [openVoidDialog, setOpenVoidDialog] = useState<any>(null);
  const [openExceptions, setOpenExceptions] = useState<any>(null);
  const [optionalComment, setOptionalComment] = useState('');
  const [selectedVoidReason, setSelectedVoidReason] = useState<any>(null);
  const [actionOwner, setActionOwner] = useState<ActionOwnerType>('All');
  const { getUserInfo } = useAuthentication();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: voidEncounter } = usePartnerVoidEncounter();

  const { mutateAsync: getStatusList } = usePartnerGetStatusList();
  const { voidReasonsHRZN, voidReasonsIBIS } = useGetVoidReasons();

  useMswManager([
    HrznGatewayMswBuilder.create().get(
      ROUTES.PartnerGetEncounterHeaderVoidReasonList,
      {
        path: ':id',
        data: []
      }
    ),
    HrznGatewayMswBuilder.create().post(
      ROUTES.PartnerGetMyEncounterMasterView,
      {
        data: makeData()
      }
    ),
    HrznGatewayMswBuilder.create().post(ROUTES.PartnerUpSertEncounterComment, {
      data: {
        message: 'Comment submitted successfully',
        success: true
      }
    })
  ]);

  useEffect(() => {
    const fetchOptions = async () => {
      const user = getUserInfo();
      const facilityIDs = user.facilities.map(
        ({
          facilityID,
          facilityName
        }: {
          facilityID: unknown;
          facilityName: unknown;
        }) => ({
          value: facilityID,
          label: facilityName
        })
      );

      // Use the hook to get status list
      const statusList = await getStatusList(
        facilityIDs.map((x: { value: unknown }) => x.value)
      );

      setStatusList(
        statusList.map((el: { description: string }) => el.description)
      );
      setFacilityOptions(facilityIDs);
    };
    fetchOptions();
  }, [getUserInfo, getStatusList]);

  useEffect(() => {
    const fetchData = async () => {
      const payload = {
        facilityIds: facility.map((el: { value: unknown }) => el.value),
        statusFilter: mapStatusFilter(status),
        key: mapAdvancedFilter(advancedFilter),
        value1: value1
          ? advancedFilter === 'Date Of Service Range'
            ? moment(value1).format('MM-DD-YYYY')
            : value1
          : '',
        value2: value2
          ? advancedFilter === 'Date Of Service Range'
            ? moment(value2).format('MM-DD-YYYY')
            : value2
          : '',
        actionOwner: mapActionOwner(actionOwner)
      };
      const config = {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('usertoken')}`
        }
      };
      const URL = `${process.env.REACT_APP_URL}/?route=${ROUTES.PartnerGetMyEncounterMasterView}`;
      const isFetching = search || refetch;
      let result = encounters;
      try {
        result = isFetching ? await axios.post(URL, payload, config) : result;
      } catch (error) {
        const errorMsg = (error as ErrorResponse)?.response?.data?.Error;
        if (errorMsg) {
          enqueueSnackbar(errorMsg, { variant: 'error' });
        }
      }

      setEncounters(isFetching ? result?.data || [] : result);
      setCurrentStatus(isFetching ? status : currentStatus);
      setNotSearched(isFetching ? false : notSearched);
      setSearch(false);
    };
    fetchData();
  }, [search, refetch]);

  useEffect(() => {
    setIsLoading(false);
  }, [encounters]);

  useEffect(() => {
    if (!openComment) return;
    const fetchComments = async (payload: any) => {
      const shouldRefetchWithPayload = true;
      const hasPayloadLength = !!payload && Object.keys(payload).length === 3;
      const hasNoComments = !comments.length;

      const triggerFetch =
        shouldRefetchWithPayload || (hasNoComments && hasPayloadLength);
      if (!triggerFetch) {
        setComments(comments);
        setRefetch(false);
        return;
      }

      const result = await getEncounterComments(payload);
      setRefetch(false);
      setComments(result);
    };
    fetchComments(openComment);
  }, [openComment, refetch]);

  const handleSubmitComment = async (submitComment: boolean) => {
    setSubmitComment(true);
    try {
      const result = await submitCommentToEncounter(submitComment);
      enqueueSnackbar(result?.message || 'Comment submitted successfully', {
        variant: 'success'
      });
      setNewComment(submitComment ? '' : newComment);
      setRefetch(submitComment ? true : refetch);
    } catch (error) {
      const errorMsg =
        (error as ErrorResponse)?.response?.data?.Error ||
        'Failed to submit comment';
      enqueueSnackbar(errorMsg, { variant: 'error' });
    } finally {
      setSubmitComment(false);
    }
  };

  const handleVoidEncounter = async () => {
    try {
      await voidEncounter({
        encounterHeaderIdentifier: openVoidDialog,
        encounterHeaderVoidReasonID:
          selectedVoidReason?.encounterHeaderVoidReasonID || 0,
        comment: optionalComment
      });
      enqueueSnackbar('Encounter voided successfully', { variant: 'success' });
      setOptionalComment('');
      setSelectedVoidReason(null);
      setOpenVoidDialog(null);
      setRefetch(true);
    } catch (error) {
      const errMsg =
        (error as ErrorResponse)?.response?.data?.Error ||
        'Failed to void encounter';
      enqueueSnackbar(errMsg, { variant: 'error' });
    }
  };

  const user = getUserInfo();

  const _mappedEncounters = useMemo(
    () =>
      encounters?.map((encounter: any) => {
        return {
          ...encounter,
          dateOfService: encounter.dateOfService
            ? moment(encounter.dateOfService).format('MM/DD/YY')
            : '',
          dateHeld: encounter.dateHeld
            ? moment(encounter.dateHeld).format('MM/DD/YY')
            : '',
          nextCheckDate: encounter.nextCheckDate
            ? moment(encounter.nextCheckDate).format('MM/DD/YY')
            : '',
          patientDOB: encounter.patientDOB
            ? moment(encounter.patientDOB).format('MM/DD/YYYY')
            : ''
        };
      }) || [],
    [encounters]
  );

  const submitCommentToEncounter = async (submitComment: boolean) => {
    const user = getUserInfo();
    const payload = {
      encounterHeaderIDList: [openComment],
      encounterHeaderCommentTypeID: selectedPreset.encounterHeaderCommentTypeID,
      externalUserID: user && user.externalUserID,
      internalUserID: 0,
      isPrivate: false,
      internalUserName: '',
      externalUserName: user && user.externalUserName,
      comment: (newComment || '').trim()
        ? `${selectedPreset?.encounterHeaderCommentType}: ${newComment.trim()}`
        : selectedPreset.encounterHeaderCommentType,
      createdDateTime: new Date(),
      isInternalUser: false
    };

    if (submitComment) {
      const { data } = await authenticatedAxios({
        method: 'post',
        url: '',
        data: payload,
        params: {
          route: ROUTES.PartnerUpSertEncounterComment
        }
      });
      return data;
    }
  };

  if (!user.showMyEncounters) {
    return <Navigate to="/MyTransactionsSummary" />;
  }

  return (
    <Layout>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div
          style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}
        >
          <h1 style={{ marginRight: '30px' }}>My Encounters</h1>
          <p
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: theme.palette.primary.main
            }}
          >
            Please select Search Type criteria and click Search
          </p>
        </div>
        {/* eslint-disable-next-line react/jsx-no-target-blank */}
        <a
          style={{ fontSize: '16px' }}
          href={`https://mgablobstorageprodcdn.blob.core.windows.net/${(
            process.env.REACT_APP_NODE_ENV || ''
          ).toLowerCase()}-cdn/Partner.Gottlieb.com.UserGuide.MyEncounters.pdf?id=${Math.random()
            .toString(36)
            .slice(2)}`}
          target="_blank"
        >
          User Guide
        </a>
      </div>
      <MyEncountersSearchContext.Provider
        value={
          {
            facilityOptions,
            setFacilityOptions,
            facility,
            setFacility,
            status,
            setStatus,
            statusList,
            advancedFilter,
            setAdvancedFilter,
            value1,
            setValue1,
            value2,
            setValue2,
            search,
            setSearch,
            isLoading,
            setIsLoading,
            setNotSearched,
            refetch,
            setRefetch,
            actionOwner,
            setActionOwner
          } as any
        }
      >
        <MyEncountersSearchForm />
        <Divider variant="middle" sx={{ mt: '2em', mb: '1em' }} />
      </MyEncountersSearchContext.Provider>
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '15px'
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <div style={{ display: notSearched ? 'none' : 'block' }}>
          <MyEncountersSearchContext.Provider
            value={
              {
                refetch,
                setRefetch,
                setEncounters,
                status
              } as any
            }
          >
            <MyEncountersTable
              status={currentStatus}
              tableData={_mappedEncounters}
              handleComments={(rowId: any, dateOfService: any) => {
                setOpenComment(rowId);
                setOpenCommentDOS(moment(dateOfService).format('MM/DD/YYYY'));
              }}
              handleVoid={(rowId: any) => {
                setOpenVoidDialog(rowId);
              }}
              handleOpenExceptions={(rowId: any) => setOpenExceptions(rowId)}
              openRecord={openComment || openVoidDialog || openExceptions}
              notSearched={notSearched}
              refetch={() => setRefetch(true)}
            />
            <Comments
              outsidePreset={selectedPreset}
              show={!!openComment}
              comments={comments}
              dateOfService={openCommentDOS}
              isSubmitting={submitComment}
              onSubmit={() => handleSubmitComment(true)}
              newComment={newComment}
              onChange={(e: { target: { value: string } } | null) => {
                if (!e?.target?.value) return setNewComment('');
                setNewComment(e.target.value);
              }}
              onChangePreset={(value: any) => {
                if (!value) return setSelectedPreset({});
                if (value.encounterHeaderCommentTypeID) {
                  setSelectedPreset(value);
                }
              }}
              onClose={() => {
                setOpenComment(null);
                setRefetch(false);
                setComments([]);
              }}
            />
            {!!openVoidDialog && (
              <VoidDialog
                show={!!openVoidDialog}
                voidReasons={
                  openVoidDialog
                    ? openVoidDialog.applicationID === 1
                      ? voidReasonsHRZN
                      : voidReasonsIBIS
                    : []
                }
                isIBIS={!openVoidDialog || openVoidDialog.applicationID === 4}
                selectedVoidReason={selectedVoidReason}
                handleVoidReasonChange={(e: { target: { value: unknown } }) =>
                  setSelectedVoidReason(e.target.value)
                }
                optionalComment={optionalComment}
                handleOptionalCommentChange={(e: {
                  target: { value: string };
                }) => setOptionalComment(e.target.value)}
                onSave={handleVoidEncounter}
                headerIDs={[openVoidDialog]}
                onClose={() => {
                  setOpenVoidDialog(null);
                  setSelectedVoidReason(null);
                  setOptionalComment('');
                  setRefetch(false);
                }}
              />
            )}
            <ExceptionsDialog
              open={!!openExceptions}
              handleClose={() => setOpenExceptions(null)}
              encounterHeaderId={openExceptions}
              facilityId={facility[0]?.value}
            />
          </MyEncountersSearchContext.Provider>
        </div>
      )}
      <SessionTimeout />
    </Layout>
  );
};

const mapAdvancedFilter = (advancedFilter: string) => {
  switch (advancedFilter) {
    case 'Date Of Birth':
      return 'PatientDOB';
    case 'Patient Last Name':
      return 'LastName';
    case 'Account Number':
      return 'FacilityAccountNumber';
    case 'Date Of Service Range':
      return 'DateOfServiceRange';
    // case 'Responsible Provider':
    //   return 'ResponsibleProvider'
    default:
      return '';
  }
};

const Layout = styled('div')`
  max-width: 2048px;
  margin: 0 auto;
  padding: 0 30px;
`;

const mapActionOwner = (actionOwner: string): number => {
  actionOwner = actionOwner.toLowerCase();
  switch (actionOwner) {
    case 'all':
      return 0;
    case 'client':
      return 1;
    case 'ventra':
      return 2;
    default:
      return -1;
  }
};

const mapStatusFilter = (statusFilter: string): string => {
  switch (statusFilter) {
    case 'On Hold':
      return 'OnHold';
    case 'Pre-Coding Authorization':
      return 'PreCodingAuthorization';
    default:
      return statusFilter;
  }
};

// Hook to get void reasons for both IBIS and HRZN systems
function useGetVoidReasons() {
  // Use the existing hooks for each system type
  const ibisHook = usePartnerGetEncounterHeaderVoidReasonList('4'); // IBIS
  const hrznHook = usePartnerGetEncounterHeaderVoidReasonList('1'); // HRZN

  return {
    voidReasonsIBIS: ibisHook.data,
    voidReasonsHRZN: hrznHook.data,
    isLoading: ibisHook.isLoading || hrznHook.isLoading
  };
}

function usePartnerGetMyEncounterComments() {
  const Axios = useAuthenticatedAxios();
  const snackbar = useSnackbar();

  useMswManager(
    HrznGatewayMswBuilder.create().post(ROUTES.PartnerGetMyEncounterComments, {
      data: [
        {
          comment: 'This is a test comment',
          createdDateTime: '2024-03-05T12:00:00Z',
          isInternalUser: false,
          createdByUserName: 'John Doe'
        },
        {
          comment: 'This is an internal comment',
          createdDateTime: '2024-03-05T13:00:00Z',
          isInternalUser: true,
          createdByUserName: 'Internal User'
        }
      ]
    })
  );

  return useMutation<unknown, ErrorResponse, {}>(
    async payload => {
      const { data } = await Axios({
        method: 'post',
        url: '',
        data: payload,
        params: {
          route: ROUTES.PartnerGetMyEncounterComments
        }
      });
      return data;
    },
    {
      onError: err => {
        snackbar.enqueueSnackbar(
          err.message ?? ROUTES.PartnerGetMyEncounterComments,
          { variant: 'error' }
        );
      }
    }
  );
}

export default MyEncounters;
